import type { IApiError } from '@services/types';

import type { TClassesBuilderArgument } from './types';

export const classesBuilder = (...args: TClassesBuilderArgument[]): string => {
  return args
    .flatMap((arg) => {
      if (typeof arg === 'string') {
        return arg;
      }
      if (typeof arg === 'object' && arg !== null) {
        return Object.keys(arg).filter((className) => {
          return arg[className];
        });
      }
      return [];
    })
    .join(' ');
};

// Format number with options for style, currency, locale, and fraction digits
//  formatNumber(2245509) -> "2.245.509" (DE)
//  formatNumber("2245509") -> "2.245.509"
//  formatNumber(12345.67, { style: 'currency', currency: 'EUR' }) -> "12.345,67 €"
//  formatNumber(0.0259781, { style: 'percent' }) -> "2,60 %"
//  formatNumber(0.0259781, { style: 'percent', fractionDigits: 1 }) -> "2,6 %"
export const formatNumber = (
  value: number | string,
  options?: {
    style?: 'number' | 'currency' | 'percent';
    currency?: string;
    locale?: string;
    fractionDigits?: number;
  }
) => {
  const locale = options?.locale ?? 'de-DE';
  const style = options?.style ?? 'number';
  const currency = options?.currency ?? 'EUR';
  const fractionDigits = options?.fractionDigits;

  const num = typeof value === 'number' ? value : Number(value);
  if (isNaN(num)) {
    return String(value);
  }

  switch (style) {
    case 'currency':
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: fractionDigits ?? 2,
        maximumFractionDigits: fractionDigits ?? 2,
      }).format(num);

    case 'percent':
      return new Intl.NumberFormat(locale, {
        style: 'percent',
        minimumFractionDigits: fractionDigits ?? 2,
        maximumFractionDigits: fractionDigits ?? 2,
      }).format(num);

    case 'number':
    default:
      return new Intl.NumberFormat(locale, {
        maximumFractionDigits: fractionDigits ?? 0,
      }).format(num);
  }
};

export const isAppApiError = (error: unknown): error is IApiError => {
  return (
    typeof error === 'object' && error !== null && 'error' in error && typeof (error as IApiError).error === 'string'
  );
};

export const parseLoginError = (error: unknown): string => {
  let translatedMessage = 'Something went wrong, please try again later.';
  if (isAppApiError(error)) {
    const status = typeof error.status === 'number' ? error.status : undefined;
    const isAuthError = status === 404 || status === 401;
    // TODO: handle invalid input format error here error.status = 400 means invalid format.

    if (isAuthError) {
      translatedMessage = 'Incorrect Report ID or Key';
    }
  }

  return translatedMessage;
};
